import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  AlertCircle,
  CheckCircle,
  FileText,
  MapPin,
  Truck,
  User,
} from "lucide-react";
import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { usePaperBillList } from "../../hooks";
import PaperBillDistributionDashboard from "./paperBillDistributionDashboard";
import AssignDistributionModal from "./assignDistributionModal";
import { Plus, UserPlus } from "lucide-react";

interface SubAreaData {
  subAreaCode: string;
  area: string;
  subArea: string;
  totalPremiseCount: number;
  totalBillCount: number;
  assignedToName: string;
  premiseCount: number;
  status: number;
  updatedBy: string;
  updatedOn: string;
  billRuns: number[];
}

interface PaperBillDistributionProps {
  cycleId: string;
}

export function PaperBillDistribution({ cycleId }: PaperBillDistributionProps) {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Read URL params
  const searchQuery = searchParams.get("query") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: billListData } = usePaperBillList(params.id,searchQuery);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on API data
  useEffect(() => {
    const totalItems = billListData?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [billListData?.count, currentPage]);

  // Handle search
  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.query = searchTerm.trim();
    } else {
      delete newParams.query;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return (
          <Badge variant="secondary">
            <AlertCircle className="w-3 h-3 mr-1" /> Unassigned
          </Badge>
        );
      case 1:
        return (
          <Badge variant="outline">
            <User className="w-3 h-3 mr-1" /> Assigned (In-house)
          </Badge>
        );
      case 2:
        return (
          <Badge variant="outline">
            <Truck className="w-3 h-3 mr-1" /> Assigned (External)
          </Badge>
        );
      case 3:
        return (
          <Badge
            variant="outline"
            className="bg-green-100 text-green-800 border-green-200"
          >
            <CheckCircle className="w-3 h-3 mr-1" /> Completed
          </Badge>
        );
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const columns: TableColumn<SubAreaData>[] = [
    {
      key: "area",
      header: "Area",
      render: (value: any, row: SubAreaData) => (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          <div>
            <div className="font-medium">{row.area || "N/A"}</div>
            <div className="text-xs text-muted-foreground">
              {row.totalPremiseCount || 0} premises • {row.totalBillCount || 0} bills
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "subArea",
      header: "Sub-Area",
      render: (value: string) => <span className="font-medium">{value || "N/A"}</span>,
    },
    {
      key: "premiseCount",
      header: "Premises",
    },
    {
      key: "totalBillCount",
      header: "Bills",
    },
    {
      key: "status",
      header: "Status",
      render: (value: number) => getStatusBadge(value),
    },
    {
      key: "distributionInfo",
      header: "Assigned To",
      render:(value:any,row:any) => <span>{row.distributionInfo?.serviceProvider || "N/A"}</span>
    },
    {
      key: "updatedBy",
      header: "Updated By",
    },
    {
      key: "updatedOn",
      header: "Updated On",
    },
    {
      key: "actions",
      header: "Actions",
      render: (value: any, row: SubAreaData) => {
        return (
          <div className="flex justify-center">
            <Button
              variant="outline"
              className="border-none h-8 w-8 p-0"
              title="Assign Distributor"
              onClick={() => {
                setSelectedAreas([row.subAreaCode]);
                setIsModalOpen(true);
              }}
            >
              <UserPlus className="w-4 h-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Paper Bill Distribution Dashboard
              </CardTitle>
              <CardDescription className="mt-1">
                Overview of paper bill distribution across all areas and sub-areas
                with assignment and progress tracking.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          
          <PaperBillDistributionDashboard />
          {selectedAreas.length >= 2 && (
            <div className="bg-muted/30 w-full rounded-md border border-border mb-6 p-2">
              <CardContent className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  {`${selectedAreas.length} subarea(s) selected`}
                </span>
                <Button 
                  onClick={() => setIsModalOpen(true)} 
                  size="sm"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Distributor
                </Button>
              </CardContent>
            </div>
          )}
          <GenericTable
            data={billListData?.result ?? []}
            columns={columns}
            rowKey="subAreaCode"
            selectable={true}
            selectedRows={selectedAreas}
            onSelectionChange={setSelectedAreas as any}
            emptyMessage="No distribution data found"
            pagination={pagination}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            searchPlaceholder="Search by area, sub-area, or assigned person..."
          />
        </CardContent>
      </Card>

      <AssignDistributionModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        selectedAreas={selectedAreas}
        onSuccess={() => setSelectedAreas([])}
      />
    </div>
  );
}
